import {
  Component,
  computed,
  effect,
  inject,
  model,
  ModelSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  ColTypeColDefMap,
  ConfirmationDialogComponent,
  CSV_EXPORT_ACTION,
  DialogService,
  DisplayedColumns,
  EControlActions,
  EsvgFiles,
  ICustomGridRow,
  IRowDataChange,
  ISelectFormOption,
  ITableAction,
  NEW_ENTRY_ACTION,
  TableApiAdapter,
  TableControlComponent,
  TableControlModule,
  TDBObject,
} from 'frontier';
import ControlGuids from '../../../api/ControlGuids';
import {Observable, of, pairwise, Subject} from 'rxjs';
import {filter, take, tap} from 'rxjs/operators';
import {LanguageSelectionDialogComponent} from './language-selection-dialog/language-selection-dialog.component';
import {LanguageCheckService, LanguageTableService} from '../../../api/generated';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AlertService} from './alert.service';

class LanguageApiAdapter extends TableApiAdapter {
  nameIndex: number;
  defaultIndex: number;

  override from(apiData: any): any {
    this.nameIndex = apiData.displayedColumns.indexOf(apiData.displayedColumns
      .find((col: DisplayedColumns): boolean => {
        return col.attribute === 'name';
      }));
    this.defaultIndex = apiData.displayedColumns.indexOf(apiData.displayedColumns
      .find((col: DisplayedColumns): boolean => {
        return col.attribute === 'UseDefault';
      }));
    return {
      displayedColumns: apiData.displayedColumns,
      columnDefs: this.createGridCols(apiData.displayedColumns),
      name: null,
      objecttype: apiData.objecttype,
      entityName: null,
      parentobj: apiData.parentobj,
    };
  }
}

@Component({
  selector: 'app-language-table',
  standalone: true,
  templateUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  imports: [TableControlModule],
  providers: [{
    provide: TableControlComponent,
    useExisting: LanguageTableComponent,
  }],
})
export class LanguageTableComponent extends TableControlComponent {
  languageTableService: LanguageTableService = inject(LanguageTableService);
  private _dialogService: DialogService = inject(DialogService);
  private _languageCheckService: LanguageCheckService = inject(LanguageCheckService);
  private _alertService: AlertService = inject(AlertService);
  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private languageChangeComplete$: Subject<void> = new Subject<void>();

  override GUID: string = ControlGuids.TLanguageTable;
  override apiAdapter: LanguageApiAdapter = new LanguageApiAdapter({}, ColTypeColDefMap);

  isDefaultLanguageSelected: Signal<boolean> = computed((): boolean => {
    const selected: ICustomGridRow[] = this.selectedRows();
    return selected.some((row: ICustomGridRow): boolean => {
      return row[this.apiAdapter.defaultIndex]?.value;
    });
  });

  languageRepo: WritableSignal<ISelectFormOption[]> = signal<ISelectFormOption[]>([]);

  private updateLanguageRepo(): void {
    this.getAllRows$().subscribe((rows: ICustomGridRow[]): void => {
      const selectedRows: ICustomGridRow[] = this.selectedRows();

      if (this.apiAdapter.nameIndex === undefined || this.apiAdapter.nameIndex === -1
        || this.apiAdapter.defaultIndex === undefined || this.apiAdapter.defaultIndex === -1) {
        this.languageRepo.set([]);
        return;
      }

      const result: ISelectFormOption[] = rows
        .map((row: ICustomGridRow): ISelectFormOption => {
          return {
            name: row[this.apiAdapter.nameIndex].value,
            value: {
              ...row.apiRow.obj,
              isDefault: row[this.apiAdapter.defaultIndex]?.value,
              isSelected: selectedRows
                .some((r: ICustomGridRow): boolean => r.apiRow.obj.signature === row.apiRow.obj.signature),
            },
          };
        });
      this.languageRepo.set(result);
    });
  }

  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    NEW_ENTRY_ACTION,
    {
      displayName: 'Löschen',
      controlAction: EControlActions.confirm,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: false,
      color: 'warn',
      icon: EsvgFiles.delete,
      isDisabled: (): boolean => {
        return this.isDefaultLanguageSelected();
      },
      disabledInteractive: true,
      tooltip: (): string => {
        return this.isDefaultLanguageSelected() ?
          'Die Standardsprache kann nicht gelöscht werden, bitte ändern Sie zuerst die Standardsprache.' : null;
      },
      action: (): Observable<boolean> => this.deleteSelectedRows(),
    },
    CSV_EXPORT_ACTION,
    {
      displayName: 'Standardsprache wechseln',
      controlAction: EControlActions.change,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.edit,
      action: (): Observable<boolean> => this.changeDefaultLanguage(),
    },
  ]);

  constructor() {
    super();

    effect((): void => {
      this.selectedRows();
      this.updateLanguageRepo();
    }, {allowSignalWrites: true});

    this.subs.add(
      this.rowDataChanged$.pipe(
        filter((data: IRowDataChange): boolean => data !== null)
      ).subscribe((): void => this.updateLanguageRepo())
    );

    this.subs.add(
      this.rowChanged$.pipe(
        filter((data: void): boolean => data !== null)
      ).subscribe((): void => this.updateLanguageRepo())
    );

    // Wait for languageChangeComplete, then request changeDefault
    this.languageChangeComplete$.subscribe((): void => {
      this.languageTableService.languageTableChangeDefaultLanguage({
        InstanceId: this.instanceId(),
      }).subscribe((res: boolean): void => {
        if (res) {
          this._languageCheckService.languageCheckGetChangesNeededForInvoices().subscribe((res: TDBObject[]): void => {
            this._alertService.invoiceAlertObjects.set(res);
          });

          this._languageCheckService.languageCheckGetChangesNeededForDunning().subscribe((res: TDBObject[]): void => {
            this._alertService.dunningAlertObjects.set(res);
          });
        }
      });
    });
  }

  deleteSelectedRows(): Observable<boolean> {
    if (this.isDefaultLanguageSelected()) {
      this._snackBar.open(
        'Die Standardsprache kann nicht gelöscht werden, bitte ändern Sie zuerst die Standardsprache.',
        'OK',
        {
          duration: 10000,
          verticalPosition: 'top',
        },
      );
      return of(false);
    }
    return this._dialogService.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Wollen Sie die ausgewählten Sprachen wirklich löschen?',
        description: 'Die Löschung kann nicht rückgängig gemacht werden. Dies beinhaltet für die Sprache ' +
          'konfigurierte Rechnungs- und Mahnungsvorlagen sowie Übersetzungen.',
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nein',
      },
    }).afterClosed()
      .pipe(
        tap((res: any): void => {
          if (res) {
            this.callActionFunction(EControlActions.delete);
          }
        }),
      );
  }

  changeDefaultLanguage(): Observable<any> {
    return this._dialogService.dialog.open(LanguageSelectionDialogComponent, {
      data: {
        languages: this.languageRepo().filter((lang: ISelectFormOption): boolean => lang.value.isDefault === false),
        title: 'Standardsprache ändern',
        description: 'Geben Sie an, welche Sprache als Standardsprache verwendet werden soll. Diese wird für alle ' +
          'Länder verwendet, denen nicht explizit eine Sprache zugewiesen ist.',
        confirmText: 'Übernehmen Sie die Auswahl. Die vorherige Auswahl wird dadurch überschrieben.',
        cancelText: 'Behalten Sie die aktuelle Auswahl weiterhin bei.',
      },
    }).afterClosed()
      .pipe(
        tap((res: TDBObject): void => {
          if (res) {
            // Patch the filter and trigger changeInstanceAndFetch request with this
            this.patchFilter({
              parent: res
            });

            // Wait for changeInstanceAndFetch response
            this.loading$.pipe(
              pairwise(),
              filter(([prev, curr]: [boolean, boolean]): boolean => prev === true && curr === false),
              take(1),
            ).subscribe((): void => {
              this.languageChangeComplete$.next();
            });
          }
        }),
      );
  }
}
